import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBlogs } from "../../slice/blogSlice";
import { useNavigate } from "react-router-dom";

const BlogPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { blogs = [], loading, error } = useSelector((state) => state.blog);

  const [visibleCount, setVisibleCount] = useState(8); // initially show 8 blogs
  const [animatedBlogIds, setAnimatedBlogIds] = useState([]);

  const newBlogsRef = useRef(null);

  useEffect(() => {
    if (!blogs || blogs.length === 0) {
      dispatch(fetchBlogs());
    }
  }, [dispatch, blogs]);

  const blogsToShow = blogs.slice(0, visibleCount);
  const showViewButton = blogs.length > 8;

  const handleToggleView = () => {
    const previousCount = visibleCount;
    const nextCount = visibleCount >= blogs.length ? 8 : visibleCount + 8;

    const newAnimatedIds = blogs
      .slice(previousCount, nextCount)
      .map((blog) => blog._id || blog.id);

    setVisibleCount(nextCount);
    setAnimatedBlogIds((prev) => [...prev, ...newAnimatedIds]);

    // Scroll to the first newly added blog
    setTimeout(() => {
      if (newBlogsRef.current) {
        newBlogsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 300);
  };

  const handleReadMore = (id) => {
    navigate(`/blog/${id}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Header and View More/Less */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#fc3c04]">Blogs <span className="text-[#345bf3]">FOICT</span></h1>

        {showViewButton && (
          <button
            onClick={handleToggleView}
            className="text-white border border-[#686104] bg-[#686104] px-6 py-2 rounded-full hover:bg-[#fc3c04] transition whitespace-nowrap"
          >
            {visibleCount >= blogs.length ? "View Less" : "View More"}
          </button>
        )}
      </div>

      {/* Blog Cards Grid */}
      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : error ? (
        <p className="text-center text-red-500">Error: {error}</p>
      ) : blogsToShow.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {blogsToShow.map((blog, index) => {
            const blogId = blog._id || blog.id;
            if (!blogId) return null;

            const isAnimated = animatedBlogIds.includes(blogId);
            const fadeInClass = isAnimated ? "animate-fade-in" : "";

            return (
              <div
                key={blogId}
                ref={isAnimated && index === visibleCount - 8 ? newBlogsRef : null}
                className={`bg-white rounded-lg overflow-hidden shadow hover:shadow-lg transition duration-300 flex flex-col ${fadeInClass}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL_REL}blogs/${blog.image}`}
                  alt={blog.title}
                  className="h-48 w-full object-cover"
                />
                <div className="p-4 flex-grow flex flex-col">
                  <h2 className="text-lg font-semibold text-[#fc3c04] line-clamp-2 mb-2">
                    {blog.title}
                  </h2>
                  <button
                    onClick={() => handleReadMore(blogId)}
                    className="mt-auto text-[#345bf3] text-sm font-medium hover:underline"
                  >
                    Read More →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-gray-500">No blogs found.</p>
      )}
    </div>
  );
};

export default BlogPage;
